import { Fragment, memo, useMemo, useRef, type KeyboardEvent, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { SettingsNavigationCard } from './SettingsNavigationCard.js';

export interface SettingsNavigationItem {
  readonly id: string;
  readonly icon: LucideIcon;
  readonly title: string;
  readonly description: string;
  readonly badge?: ReactNode;
  readonly disabled?: boolean;
}

export interface SettingsNavigationProps {
  readonly items: readonly SettingsNavigationItem[];
  readonly activeId: string;
  readonly navAriaLabel: string;
  readonly onSelect: (id: string) => void;
  readonly idPrefix?: string;
  readonly className?: string;
  /**
   * Compact exclusive accordion: render `accordionPanel` directly under the
   * active card when `accordionExpanded` is true (single-column nav only).
   */
  readonly accordionMode?: boolean;
  readonly accordionExpanded?: boolean;
  readonly accordionPanel?: ReactNode;
}

export const SettingsNavigation = memo(
  ({
    items,
    activeId,
    navAriaLabel,
    onSelect,
    idPrefix = 'settings',
    className = '',
    accordionMode = false,
    accordionExpanded = false,
    accordionPanel = null,
  }: SettingsNavigationProps): JSX.Element => {
    const rootClass = ['asp-settings-nav-grid', className].filter((part) => part.length > 0).join(' ');
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const enabledIndexes = useMemo(
      () => items.map((item, index) => (item.disabled ? -1 : index)).filter((index) => index >= 0),
      [items],
    );

    const moveFocusToEnabledTab = (targetIndex: number): void => {
      tabRefs.current[targetIndex]?.focus();
    };

    const findCurrentEnabledPosition = (currentIndex: number): number => {
      const position = enabledIndexes.indexOf(currentIndex);
      return position >= 0 ? position : 0;
    };

    const getNextEnabledIndex = (currentIndex: number, direction: 1 | -1): number | null => {
      if (enabledIndexes.length === 0) {
        return null;
      }
      const currentPosition = findCurrentEnabledPosition(currentIndex);
      const nextPosition =
        (currentPosition + direction + enabledIndexes.length) % enabledIndexes.length;
      return enabledIndexes[nextPosition] ?? null;
    };

    const handleTabKeyDown =
      (index: number) =>
      (event: KeyboardEvent<HTMLButtonElement>): void => {
        if (enabledIndexes.length === 0) {
          return;
        }

        let nextIndex: number | null = null;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            nextIndex = getNextEnabledIndex(index, 1);
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            nextIndex = getNextEnabledIndex(index, -1);
            break;
          case 'Home':
            nextIndex = enabledIndexes[0] ?? null;
            break;
          case 'End':
            nextIndex = enabledIndexes[enabledIndexes.length - 1] ?? null;
            break;
          default:
            return;
        }

        if (nextIndex == null || nextIndex === index) {
          return;
        }

        event.preventDefault();
        moveFocusToEnabledTab(nextIndex);
      };

    return (
      <div
        className={rootClass}
        role="tablist"
        aria-label={navAriaLabel}
        data-testid="settings-sub-nav"
        data-accordion-mode={accordionMode ? 'true' : undefined}
      >
        {items.map((item, index) => {
          const isActive = item.id === activeId;
          const isExpanded = accordionMode && isActive && accordionExpanded;
          const showAccordionPanel = isExpanded && accordionPanel != null;

          return (
            <Fragment key={item.id}>
              <SettingsNavigationCard
                icon={item.icon}
                title={item.title}
                description={item.description}
                active={isActive}
                badge={item.badge}
                disabled={item.disabled}
                panelId={`${idPrefix}-panel-${item.id}`}
                tabId={`${idPrefix}-tab-${item.id}`}
                tabIndex={isActive ? 0 : -1}
                tabRef={(element) => {
                  tabRefs.current[index] = element;
                }}
                onKeyDown={handleTabKeyDown(index)}
                onClick={() => onSelect(item.id)}
                accordionMode={accordionMode}
                expanded={isExpanded}
              />
              {showAccordionPanel ? (
                <div
                  className="asp-settings-nav-accordion-panel twz-shell__panel"
                  role="tabpanel"
                  id={`${idPrefix}-panel-${item.id}`}
                  aria-labelledby={`${idPrefix}-tab-${item.id}`}
                  data-testid="settings-accordion-panel"
                >
                  {accordionPanel}
                </div>
              ) : null}
            </Fragment>
          );
        })}
      </div>
    );
  },
);

SettingsNavigation.displayName = 'SettingsNavigation';
